import {EventEmitter, Injectable} from '@angular/core';
import {IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommand} from '@shared/commands/command-base';
import {CommandService} from '@core/services/command.service';
import {take} from 'rxjs/operators';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {SnackBarService} from '@core/services/snack-bar.service';
import {EventStatus} from '@shared/models/notification-message.model';
import {Router} from '@angular/router';
import {DialogService} from '@shared/services/dialog-service';
import {Api} from '@shared/constants/common-field.constants';
import {AddCompanyComponent} from '@features/admin-panel/components/add-company/add-company.component';
import {MatDialog} from '@angular/material/dialog';
import {UserInfo} from '@shared/models/user-info.model';
import {AdminPanelState} from '@features/admin-panel/store/reducers/admin-panel.reducer';
import {select, Store} from '@ngrx/store';
import {getUserInfo} from '@shared/store/selectors/security.selector';
import {Clipboard} from '@angular/cdk/clipboard';
import {FileManagementService} from "@core/services/file-management.service";

@Injectable({
  providedIn: 'root',
})
export class AdminPanelHandler implements IPlatformToolHandler {
  invalidateSubject: InvalidateSubject;
  userInfo: UserInfo;
  selectedTabModel: TabModel;
  magicLinkModel: MagicLinkModel;
  magicLinkModelValueChanged: EventEmitter<any> = new EventEmitter<any>();

  constructor(private dialog: MatDialog,
              private router: Router,
              private store: Store<AdminPanelState>,
              private commandService: CommandService,
              private snackBarService: SnackBarService,
              private dialogService: DialogService,
              private clipboard: Clipboard,
              private fileManagementService: FileManagementService,
              private fuseSplashScreenService: FuseSplashScreenService) {
  }

  destroy(): void {
    this.invalidateSubject.close();
  }

  onBeforeLoad(): void {
    this.store.pipe(select(getUserInfo), take(1)).subscribe(u => {
      this.userInfo = u;
    });
  }

  executeCommand(command: PlatformCommand) {
    if (command.name === 'RefreshGrid') {
      this.invalidateSubject.invalidate();
    } if (command.name === 'AddCompany') {
      this.addCompany();
      return;
    } if (command.fieldValues['RedirectUrl']) {
      this.router.navigateByUrl(command.fieldValues['RedirectUrl']).then();
    } else {
      this.executeCommandToApi(command);
    }
  }

  addCompany() {
    const dialogRef = this.dialog.open(AddCompanyComponent, {
      width: '600px',
      disableClose: true,
      data: {userName: this.userInfo.userName}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.save) {
        const company = result.company;
        const command = new PlatformCommand('AddCompanyPlatformCommand', Api.AdminPanel);
        command.fieldValues['Company'] = company;
        this.executeCommandToApi(command);
      }
    });
  }

  executeCommandToApi(command: PlatformCommand) {
    command = JSON.parse(JSON.stringify(command));
    this.fuseSplashScreenService.show();
    this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(response => {
      this.fuseSplashScreenService.hide();
      if (response.fieldValues['DownloadFileResponseModel']){
        const downloadResponseModel = response.fieldValues['DownloadFileResponseModel'];
        if (!downloadResponseModel) {
          console.error('Download file response model not found.');
          return;
        }
        let fileId = downloadResponseModel.fileId;
        if (!fileId) {
          fileId = downloadResponseModel.FileId;
        }
        this.fileManagementService.downloadClientFileWithId(fileId).subscribe(payload => {
          this.fuseSplashScreenService.hide();
          this.fileManagementService.openSaveFileWindow(payload, downloadResponseModel.fileName);
        });
      }
      if (response.fieldValues['SnackBarMessage']) {
        if (response.status === EventStatus.Success) {
          this.snackBarService.showInformation(response.fieldValues['SnackBarMessage']);
        } else {
          this.snackBarService.showError(response.fieldValues['SnackBarMessage']);
        }
      }
      if (response.fieldValues['ShowWarning']) {
        this.snackBarService.showWarning(response.fieldValues['ShowWarning']);
      }
      if (response.fieldValues['ShowErrorMessage']) {
        this.snackBarService.showError(response.fieldValues['ShowErrorMessage']);
      }
      if (response.fieldValues['RedirectCommand']) {
        this.executeCommand(response.fieldValues['RedirectCommand']);
        return;
      } else if (response.fieldValues['RedirectUrl']) {
        this.router.navigateByUrl(response.fieldValues['RedirectUrl']).then();
        return;
      } else if (response.fieldValues['PlatformLinkModel']) {
        this.magicLinkModelValueChanged.emit({
          id: 'PlatformToolContainerComponent',
          value: response.fieldValues['PlatformLinkModel']
        });
      } else {
        this.invalidateSubject.invalidate();
      }
    },
      error => {
        console.error('Failed ' + command.name, error);
        this.fuseSplashScreenService.hide();
      });
  }

  setSelectedTab(selectedTab: TabModel): void {
    this.selectedTabModel = selectedTab;
  }

  valueChanged($event) {
    let command = null;
    let actionId = '';
    if ($event.action && $event.action.id) {
      command = this.magicLinkModel.additionalCommands[$event.action.id];
      actionId = $event.action.id;
    }
    if (!command && $event.fieldDefinition) {
      command = this.magicLinkModel.additionalCommands[$event.fieldDefinition.key];
      actionId = $event.fieldDefinition.key;
    }
    if (command) {
      command.fieldValues['ItemId'] = $event.item.id;
      command.fieldValues['ActionId'] = actionId;
      this.executeCommand(command);
      return;
    }
    if ($event.action) {
      this.handleAction($event);
    }
  }

  private handleAction($event: any) {
    const { id: action } = $event.action;
    const { item } = $event;
    const subscriptionId = item.id;

    switch (action) {
      case 'CopyAccessKey':
        this.copyToClipboard(item);
        break;

      case 'Deactivate':
      case 'Activate':
      case 'Renew':
        this.toggleSubscription(action, subscriptionId, item);
        break;

      case 'Approve':
      case 'Decline':
        this.handlePostpaidRequest(action, item);
        break;

      default:
        this.executeAdditionalCommand(action, $event);
    }
  }

  private handlePostpaidRequest(action: string, item: any) {
    const command = new PlatformCommand("UpdatePostpaidRequestCommand", Api.AdminPanel);
    command.setValue("ItemId", item.id);
    command.setValue("Action", action);
    this.executeCommand(command);
  }

  private copyToClipboard(item: any): void {
    const accessKey = item.cells['AccessKey'].value;
    this.clipboard.copy(accessKey);
    this.snackBarService.showInformation('AccessKey Copied to clipboard');
  }

  private toggleSubscription(action: string, subscriptionId: string, item: any): void {
    this.executeSubscriptionCommand('HandleAccountSubscriptionCommand', subscriptionId, action);
  }

  private executeSubscriptionCommand(commandName: string, subscriptionId: string, action: string): void {
    const command = new PlatformCommand(commandName, Api.AdminPanel);
    command.setValue('SubscriptionId', subscriptionId);
    command.setValue('Action', action);
    this.executeCommand(command);
  }

  private executeAdditionalCommand(actionId: string, $event: any): void {
    const command = this.magicLinkModel.additionalCommands[actionId];
    if (!command) return;

    switch (command.name) {
      case 'DeleteMergeCommand':
        this.deleteMerge($event);
        break;
      case 'DeleteApiGatewayItemCommand':
        this.deleteApiGatewayItem($event);
        break;
      default:
        this.executeCommand(command);
    }
  }

  private deleteApiGatewayItem($event: any) {
    this.dialogService.showYesNoDialog('Confirm Delete', 'Are you sure you want to delete this item?').pipe(take(1)).subscribe(state => {
      if (state && state.action === 'OK') {
        const payload = $event.item.id;
        const deleteApiGatewayItemCommand = new PlatformCommand('DeleteApiGatewayItemCommand', Api.AdminPanel);
        deleteApiGatewayItemCommand.fieldValues['ApiGatewayItemId'] = payload;
        deleteApiGatewayItemCommand.isCamelCase = true;
        this.fuseSplashScreenService.show();
        this.commandService.executePlatformCommand(deleteApiGatewayItemCommand).pipe(take(1)).subscribe(response => {
          if (response.fieldValues['SnackBarMessage']) {
            this.fuseSplashScreenService.hide();
            const duration = response.fieldValues['SnackBarMessage'].length * 80;
            this.snackBarService.showInformation(response.fieldValues['SnackBarMessage'], duration);
            this.invalidateSubject.invalidate();
          }
          if (response.fieldValues['SnackBarErrorMessage']) {
            this.snackBarService.showError(response.fieldValues['SnackBarErrorMessage']);
          }
          this.fuseSplashScreenService.hide();
          this.invalidateSubject.invalidate();
        }, err => {
          this.fuseSplashScreenService.hide();
          this.snackBarService.showError('Can\'t Delete Mapping');
          console.log('error: ', err);
        });
      }
    });
  }

  private deleteMerge($event: any) {
    this.dialogService.showYesNoDialog('Confirm Delete', 'Are you sure you want to delete this merge?').pipe().subscribe(state => {
      if (state && state.action === 'OK') {
        const payload = $event.item.id;
        const deleteMergeCommand = new PlatformCommand('DeleteMergeCommand', Api.AdminPanel);
        deleteMergeCommand.fieldValues['MergeId'] = payload;
        deleteMergeCommand.isCamelCase = true;
        deleteMergeCommand.waitForResponse = true;
        this.fuseSplashScreenService.show();
        this.commandService.executePlatformCommand(deleteMergeCommand).pipe(take(1)).subscribe(response => {
          if (response.fieldValues['SnackBarMessage']) {
            this.fuseSplashScreenService.hide();
            const duration = response.fieldValues['SnackBarMessage'].length * 80;
            this.snackBarService.showInformation(response.fieldValues['SnackBarMessage'], duration);
            this.invalidateSubject.invalidate();
          }
          if (response.fieldValues['SnackBarErrorMessage']) {
            this.snackBarService.showError(response.fieldValues['SnackBarErrorMessage']);
          }
          this.fuseSplashScreenService.hide();
          this.invalidateSubject.invalidate();
        }, err => {
          this.fuseSplashScreenService.hide();
          this.snackBarService.showError('Can\'t Delete Merge');
          console.log('error: ', err);
        });
      }
    });
  }
}
